import React, { FC, MouseEvent, useRef } from 'react';
import injectSheet from 'react-jss';
import { TweenMax } from 'gsap';
import PropTypes from 'prop-types';
import MobileDetect from '../../utils/mobileDetect';

export type StepAction = 'button_clicked' | 'key_pressed';

const styleSheet = {
  stepButton: {
    fontFamily: 'CircularStd, sans-serif',
    outline: 0,
    width: '180px',
    height: '48px',
    borderRadius: '6px',
    backgroundColor: '#085fe5',
    fontWeight: '500',
    border: 'solid 1.6px #085fe5',
    color: '#fff',
    textTransform: 'uppercase',
    fontSize: '16px',
    cursor: 'pointer',
    '@media (max-width: 900px)': {
      width: '100%',
      height: '52px',
      fontWeight: 'bold',
    },
    '&:focus': {
      backgroundColor: '#085fe5',
      boxShadow: '0 4px 8px 0 rgba(56, 103, 250, 0.5)',
      borderColor: '#085fe5',
    },
  },
  keyboardShortcut: {
    color: '#b7c1e1',
    fontSize: '12px',
    display: 'inline-block',
    marginTop: '12px',
  },
  stepWrapper: {
    alignItems: 'flex-end',
    justifyContent: 'end',
    flexDirection: 'column',
    display: 'flex',
    width: '100%',
    textAlign: 'right',
    fontFamily: 'CircularStd, sans-serif',
  },
};

const hoverState = (event: MouseEvent<HTMLButtonElement>) => {
  event.stopPropagation();
  event.preventDefault();
  TweenMax.to(event.currentTarget, 0.35, {
    color: '#fff',
    backgroundColor: '#0e2c8a',
    boxShadow: '0 4px 8px 0 rgba(56, 103, 250, 0.5)',
    borderColor: '#0e2c8a',
  });
};

const hoverOffState = (
  event: MouseEvent<HTMLButtonElement>,
  buttonRef?: HTMLButtonElement
) => {
  event.stopPropagation();
  event.preventDefault();
  TweenMax.to(event?.currentTarget || buttonRef, 0.35, {
    color: '#fff',
    backgroundColor: '#085fe5',
    boxShadow: '0 4px 8px 0 rgba(56, 103, 250, 0)',
    borderColor: '#085fe5',
  });
};

interface ButtonProps {
  onKeyDown?: string;
  onAction?: (stepAction: StepAction) => void;
  classes?: any;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset' | undefined;
  children: any;
}

const Button: FC<ButtonProps> = ({
  onKeyDown,
  onAction,
  classes,
  disabled,
  type,
  children,
}: ButtonProps) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isMobile = !!(MobileDetect.phone() || MobileDetect.tablet());

  const handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === onKeyDown) {
      // Check if focused element is a list or next button, if so stop the enter key from submitting form since it is needed to navigate all list form types and if next button is in focus the enter key triggers the on click event//
      const activeElementType = document.activeElement.getAttribute(
        'data-type'
      );

      // Used to check activeElementType for list here because:
      // Check if focused element is a list or next button, if so stop the enter key from submitting form since it is needed to navigate all list form types and if next button is in focus the enter key triggers the on click event
      // While actually using this app, it does not seem necesarry for accessibility.  I am able to tab, use arrow keys, then press enter on the entry in the
      // list I want, or skip unnecessary fields altogether.  Leaving this comment in case someone comes looking for why lists could've broken.
      if (
        activeElementType !== 'next' &&
        activeElementType !== 'list' &&
        onAction
      ) {
        onAction('key_pressed');
      }
    }
  };

  return (
    <div className={classes.stepWrapper}>
      <button
        type="button"
        ref={buttonRef}
        data-type={type || 'next'}
        className={classes.stepButton}
        onClick={event => {
          if (onAction) {
            onAction('button_clicked');
          }
          hoverOffState(event);
        }}
        onMouseEnter={hoverState}
        onFocus={hoverState}
        onBlur={hoverOffState}
        onMouseLeave={hoverOffState}
        disabled={disabled}
        onKeyUp={handleKeyUp}
      >
        {children}
      </button>
      {!isMobile && (
        <div className={classes.keyboardShortcut}>
          {`press ${onKeyDown.toUpperCase()} to continue`}
        </div>
      )}
    </div>
  );
};

Button.propTypes = {
  onAction: PropTypes.func,
  disabled: PropTypes.bool,
  onKeyDown: PropTypes.string,
};

Button.defaultProps = {
  onAction: null,
  onKeyDown: 'Enter',
  classes: {},
  disabled: false,
  type: 'button',
};

export default injectSheet(styleSheet)(Button);
